import {
  Box,
  Button,
  Checkbox,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { BankAvatar } from "@dfp/react-components";

import { useTT } from "../../useTT.ts";
import {
  formatBalance,
  getReadableTextColor,
} from "../demoBankConnect.utils.ts";
import type { BankFlowProps } from "./bankFlow.types.ts";

/**
 * Fallback bank screen for providers without a faithful replica — the original
 * single-step account picker with the bank's brand colour on the CTA.
 */
export function GenericBankFlow({
  bankName,
  bic,
  brandColor,
  accounts,
  onConnect,
}: BankFlowProps) {
  const { tt } = useTT();
  const connectTextColor = getReadableTextColor(brandColor);

  // Pre-select all accounts by default.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(accounts.map((account) => account.id)),
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f1f3f5",
        display: "flex",
        alignItems: "center",
        py: 6,
      }}
    >
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, borderRadius: 3 }} elevation={2}>
          <Stack sx={{ alignItems: "center", gap: 1.5, mb: 3 }}>
            <BankAvatar bank={bic} size={56} />
            <Typography variant="h2">{bankName}</Typography>
            <Typography sx={{ color: "text.secondary", textAlign: "center" }}>
              {tt("demoBankConnect.selectAccounts")}
            </Typography>
          </Stack>

          <Stack data-tour="bank-accounts" sx={{ gap: 1 }}>
            {accounts.map((account) => (
              <Stack
                key={account.id}
                direction="row"
                onClick={() => toggle(account.id)}
                sx={{
                  alignItems: "center",
                  gap: 1.5,
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  cursor: "pointer",
                }}
              >
                <Checkbox
                  checked={selectedIds.has(account.id)}
                  sx={{
                    p: 0,
                    color: brandColor,
                    "&.Mui-checked": { color: brandColor },
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {account.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {account.iban}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatBalance(account.balance, account.currency)}
                </Typography>
              </Stack>
            ))}
          </Stack>

          <Button
            data-tour="bank-connect-submit"
            fullWidth
            variant="contained"
            disabled={selectedIds.size === 0}
            onClick={() =>
              onConnect(
                accounts.filter((account) => selectedIds.has(account.id)),
              )
            }
            sx={{
              mt: 3,
              bgcolor: brandColor,
              color: connectTextColor,
              "&:hover": {
                bgcolor: brandColor,
                color: connectTextColor,
                filter: "brightness(0.92)",
              },
            }}
          >
            {selectedIds.size > 0
              ? tt("demoBankConnect.connectWithCount", {
                  count: selectedIds.size,
                })
              : tt("demoBankConnect.connect")}
          </Button>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "center",
              color: "text.secondary",
              mt: 2,
            }}
          >
            {tt("demoBankConnect.disclaimer")}
          </Typography>
        </Paper>
      </Container>
    </Box>
  );
}
